import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Row, Col, Table, Button, Badge, Input, FormGroup, Label,
  Modal, ModalHeader, ModalBody, ModalFooter, Pagination, PaginationItem, PaginationLink
} from 'reactstrap';
import Widget from '../../components/Widget';
import config from '../../config';
import opsWebSocket from '../../services/opsWebSocket';
import { normalizeAttachment, resolveSupportAttachmentUrl } from '../../utils/supportAttachment';

/** Absolute customer API base — global axios.defaults.baseURL is ops (/api/v1/ops). */
function customerApiBase() {
  const raw = (config.customerApiBase || '/api/customer').trim() || '/api/customer';
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/$/, '');
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
  return `${origin}${raw.startsWith('/') ? raw : `/${raw}`}`.replace(/\/$/, '');
}
const API_BASE = customerApiBase();
const customerAxios = axios.create(); // no ops baseURL

const CLOSED_BY_LABELS = {
  ops: 'Support operator',
  b2b: 'B2B customer',
  customer: 'Customer',
  system: 'System (inactivity timeout)',
};

/** Ops terminal state: resolved and legacy closed are the same. */
const normalizeTicketStatusForOps = (status) => (
  status === 'closed' ? 'resolved' : status
);

const formatTicketStatusLabel = (status) => {
  const s = normalizeTicketStatusForOps(status);
  const labels = {
    raised: 'Raised',
    open: 'Open',
    in_progress: 'In Progress',
    resolved: 'Resolved & Closed',
    cancelled: 'Cancelled',
  };
  return labels[s] || (status || '').replace(/_/g, ' ');
};

const formatTelHref = (phone) => {
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) return `tel:+91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return `tel:+${digits}`;
  return `tel:${digits}`;
};

const SupportTicketsPage = () => {
  const location = useLocation();
  const deepLinkHandled = useRef(false);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const limit = 8;

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updateData, setUpdateData] = useState({ status: '', admin_notes: '' });
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const messagesEndRef = React.useRef(null);

  const fetchMessages = async (ticketId) => {
    if (!ticketId) return;
    try {
      const res = await customerAxios.get(`${API_BASE}/support-tickets/${ticketId}/messages`);
      setMessages(res.data || []);
    } catch (err) {
      console.error('Failed to fetch ticket messages', err);
    }
  };

  useEffect(() => {
    if (selectedTicket) {
      fetchMessages(selectedTicket.id);
      setAttachedFiles([]);
      setIsUploading(false);
      const interval = setInterval(() => {
        fetchMessages(selectedTicket.id);
      }, 4000); // Poll every 4 seconds
      return () => clearInterval(interval);
    } else {
      setMessages([]);
      setAttachedFiles([]);
      setIsUploading(false);
    }
  }, [selectedTicket]);


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && attachedFiles.length === 0) || !selectedTicket) return;
    try {
      await customerAxios.post(`${API_BASE}/support-tickets/${selectedTicket.id}/messages`, {
        message_text: newMessage.trim() || "",
        attachments: attachedFiles
      });
      setNewMessage('');
      setAttachedFiles([]);
      fetchMessages(selectedTicket.id);
    } catch (err) {
      toast.error('Failed to send reply');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await customerAxios.post(`${API_BASE}/support-tickets/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const payload =
        typeof res.data === 'string'
          ? {
              url: res.data,
              name: file.name,
              type: file.type?.startsWith('image/')
                ? 'image'
                : file.type?.startsWith('video/')
                  ? 'video'
                  : 'file',
            }
          : res.data;
      setAttachedFiles((prev) => [...prev, payload]);
    } catch (err) {
      console.error('Failed to upload file', err);
      toast.error('Failed to upload attachment');
    } finally {
      setIsUploading(false);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * limit;
      const res = await customerAxios.get(`${API_BASE}/support-tickets`, {
        params: { 
          status: filter !== 'all' ? filter : undefined,
          date: filterDate || undefined,
          limit, 
          offset 
        }
      });
      const newTickets = res.data.tickets || [];
      setTickets(newTickets);
      setTotalCount(res.data.total || 0);
      setSelectedIds([]);
      
      // Keep selectedTicket in sync with the fetched data (e.g. if connection request was cancelled)
      setSelectedTicket(prev => {
        if (prev) {
          const updated = newTickets.find(t => t.id === prev.id);
          return updated ? { ...prev, ...updated } : prev;
        }
        return prev;
      });
    } catch (err) {
      toast.error('Failed to fetch support tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchTickets is stable enough for filter/page/date
  }, [filter, filterDate, currentPage]);

  useEffect(() => {
    setPageInput(currentPage);
  }, [currentPage]);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const openTicket = (ticket) => {
    setSelectedTicket(ticket);
    setUpdateData({
      status: normalizeTicketStatusForOps(ticket.status),
      admin_notes: ticket.admin_notes || ''
    });
    toggleModal();
    if (!ticket.is_opened || ticket.status === 'raised') {
      customerAxios.post(`${API_BASE}/support-tickets/${ticket.id}/mark-opened`)
        .then(() => {
          const nextStatus = ticket.status === 'raised' ? 'open' : ticket.status;
          setSelectedTicket((prev) => (
            prev && prev.id === ticket.id
              ? { ...prev, is_opened: true, status: nextStatus }
              : prev
          ));
          setUpdateData((prev) => ({ ...prev, status: normalizeTicketStatusForOps(nextStatus) }));
          fetchTickets();
        })
        .catch(err => console.error('Failed to mark ticket as opened', err));
    }
  };

  const handleUpdate = async () => {
    if (!selectedTicket) return;
    try {
      await customerAxios.put(`${API_BASE}/support-tickets/${selectedTicket.id}`, updateData);
      toast.success('Ticket updated successfully');
      toggleModal();
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update ticket');
    }
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm(`Are you sure you want to permanently delete ticket TKT-${ticketId}?`)) return;
    try {
      await customerAxios.delete(`${API_BASE}/support-tickets/${ticketId}`);
      toast.success('Ticket deleted successfully');
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete ticket');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to permanently delete ${selectedIds.length} tickets?`)) return;
    try {
      await customerAxios.post(`${API_BASE}/support-tickets-bulk-delete`, { ticket_ids: selectedIds });
      toast.success(`${selectedIds.length} tickets deleted successfully`);
      setSelectedIds([]);
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to bulk delete tickets');
    }
  };
  
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(tickets.map(t => t.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (e, id) => {
    if (e.target.checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(ticketId => ticketId !== id));
    }
  };

  const handleAcceptConnection = async (ticketId, openAfterAccept = false) => {
    try {
      await customerAxios.post(`${API_BASE}/support-tickets/${ticketId}/accept-connection`);
      toast.success('Connected successfully to live support!');
      
      try {
        await customerAxios.post(`${API_BASE}/support-tickets/${ticketId}/mark-opened`);
      } catch (e) {
        console.error('Failed to mark ticket as opened', e);
      }
      
      const offset = (currentPage - 1) * limit;
      const listRes = await customerAxios.get(`${API_BASE}/support-tickets`, {
        params: { 
          status: filter !== 'all' ? filter : undefined,
          date: filterDate || undefined,
          limit, 
          offset 
        }
      });
      const updatedTickets = listRes.data.tickets || [];
      setTickets(updatedTickets);
      setTotalCount(listRes.data.total || 0);

      const target = updatedTickets.find(x => x.id === ticketId);
      if (target && (openAfterAccept || selectedTicket?.id === ticketId)) {
        setSelectedTicket(target);
        setUpdateData({
          status: normalizeTicketStatusForOps(target.status),
          admin_notes: target.admin_notes || ''
        });
        if (!isModalOpen) {
          setIsModalOpen(true);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept connection');
    }
  };

  useEffect(() => {
    if (deepLinkHandled.current || loading) return;
    const params = new URLSearchParams(location.search);
    const ticketId = params.get('ticket');
    if (!ticketId) return;

    const target = tickets.find((t) => String(t.id) === String(ticketId));
    if (!target) return;

    deepLinkHandled.current = true;
    openTicket(target);
    setIsModalOpen(true);

    if (params.get('live') === '1') {
      if (target.connect_requested_at && !target.connected_at) {
        handleAcceptConnection(target.id, true);
      } else if (target.connected_at) {
        fetchMessages(target.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time deep link when tickets load
  }, [loading, tickets, location.search]);

  const handleDeclineConnection = async (ticketId) => {
    try {
      await customerAxios.post(`${API_BASE}/support-tickets/${ticketId}/decline-connection`);
      toast.info('Connection request declined.');
      
      const offset = (currentPage - 1) * limit;
      const listRes = await customerAxios.get(`${API_BASE}/support-tickets`, {
        params: { 
          status: filter !== 'all' ? filter : undefined,
          date: filterDate || undefined,
          limit, 
          offset 
        }
      });
      const updatedTickets = listRes.data.tickets || [];
      setTickets(updatedTickets);
      setTotalCount(listRes.data.total || 0);

      const target = updatedTickets.find(x => x.id === ticketId);
      if (target && selectedTicket?.id === ticketId) {
        setSelectedTicket(target);
        setUpdateData({
          status: normalizeTicketStatusForOps(target.status),
          admin_notes: target.admin_notes || ''
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline connection');
    }
  };

  const handleCloseConnection = async (ticketId) => {
    try {
      await customerAxios.post(`${API_BASE}/support-tickets/${ticketId}/close-session`, {
        closed_by: 'ops',
      });
      toast.success('Live connection closed successfully.');
      
      const offset = (currentPage - 1) * limit;
      const listRes = await customerAxios.get(`${API_BASE}/support-tickets`, {
        params: { 
          status: filter !== 'all' ? filter : undefined,
          date: filterDate || undefined,
          limit, 
          offset 
        }
      });
      const updatedTickets = listRes.data.tickets || [];
      setTickets(updatedTickets);
      setTotalCount(listRes.data.total || 0);

      const target = updatedTickets.find(x => x.id === ticketId);
      if (target) {
        setSelectedTicket({
          ...target,
          connected_at: null,
          live_session_id: null,
          connect_requested_at: null,
          live_session_closed_by: target.live_session_closed_by || 'ops',
        });
        setUpdateData({
          status: normalizeTicketStatusForOps(target.status),
          admin_notes: target.admin_notes || ''
        });
      } else {
        setIsModalOpen(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close connection');
    }
  };

  const isLiveSessionActive = (ticket) => Boolean(ticket?.connected_at);

  useEffect(() => {
    const handleSupportRequest = () => {
      fetchTickets();
    };
    const handleSupportAccept = () => {
      fetchTickets();
    };
    const handleSupportCancel = (data) => {
      fetchTickets();
      if (selectedTicket && Number(selectedTicket.id) === Number(data.ticket_id)) {
        setSelectedTicket(prev => ({
          ...prev,
          connect_requested_at: null,
          connected_at: null,
          live_session_id: null
        }));
        toast.info('Live connection request was cancelled or declined.');
      }
    };
    const handleSupportClose = (data) => {
      fetchTickets();
      if (selectedTicket && Number(selectedTicket.id) === Number(data.ticket_id)) {
        const closedBy = data.closed_by || 'b2b';
        setSelectedTicket(prev => ({
          ...prev,
          connected_at: null,
          live_session_id: null,
          connect_requested_at: null,
          status: data.ticket_status || prev.status,
          live_session_closed_by: closedBy,
          live_session_closed_at: data.timestamp || new Date().toISOString(),
        }));
        const who = CLOSED_BY_LABELS[closedBy] || closedBy;
        toast.info(`Live support session was closed by ${who}.`);
      }
    };

    const handleNewTicket = () => fetchTickets();
    const handleSupportMessage = (data) => {
      if (selectedTicket && Number(selectedTicket.id) === Number(data.ticket_id)) {
        fetchMessages(selectedTicket.id);
      }
      fetchTickets();
    };

    const unsubNew = opsWebSocket.subscribe('support_ticket_created', handleNewTicket);
    const unsubReq = opsWebSocket.subscribe('support_connect_request', handleSupportRequest);
    const unsubAccept = opsWebSocket.subscribe('support_connect_accept', handleSupportAccept);
    const unsubCancel = opsWebSocket.subscribe('support_connect_cancel', handleSupportCancel);
    const unsubClose = opsWebSocket.subscribe('support_connect_close', handleSupportClose);
    const unsubMsg = opsWebSocket.subscribe('support_message', handleSupportMessage);

    return () => {
      unsubNew();
      unsubReq();
      unsubAccept();
      unsubCancel();
      unsubClose();
      unsubMsg();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- WS handlers intentionally omit fetchTickets
  }, [selectedTicket, currentPage, filter, filterDate]);

  const renderStatus = (status, ticket = null) => {
    const map = {
      raised: 'secondary',
      open: 'info',
      in_progress: 'warning',
      resolved: 'success',
      closed: 'success',
      cancelled: 'danger'
    };
    if (ticket && ticket.connect_requested_at && !ticket.connected_at) {
      return (
        <Badge color="danger" className="animate-pulse" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#fff' }} />
          CONNECTION REQUESTED
        </Badge>
      );
    }
    const normalized = normalizeTicketStatusForOps(status);
    return (
      <Badge color={map[normalized] || map[status] || 'primary'}>
        {formatTicketStatusLabel(status).toUpperCase()}
      </Badge>
    );
  };

  const renderPriority = (priority) => {
    const map = {
      low: 'secondary',
      medium: 'primary',
      high: 'warning',
      urgent: 'danger'
    };
    return <Badge color={map[priority] || 'primary'}>{priority.toUpperCase()}</Badge>;
  };

  const totalPages = Math.ceil(totalCount / limit);

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handlePageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const num = parseInt(pageInput, 10);
      if (Number.isFinite(num) && num >= 1 && num <= totalPages) {
        setCurrentPage(num);
      } else {
        setPageInput(currentPage);
      }
    }
  };

  const handlePageInputBlur = () => {
    setPageInput(currentPage);
  };

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <h2 className="page-title">Support Tickets</h2>
        </Col>
      </Row>

      <Row>
        <Col>
          <Widget title={<h5>Ticket Management</h5>}>
            <div className="d-flex flex-wrap justify-content-between mb-3" style={{ gap: '15px' }}>
              <div className="d-flex flex-wrap" style={{ gap: '15px' }}>
                <FormGroup className="mb-0" style={{ width: '200px' }}>
                  <Label for="statusFilter" className="sr-only">Filter</Label>
                  <Input
                    type="select"
                    id="statusFilter"
                    value={filter}
                    onChange={(e) => { setFilter(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="raised">Raised</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved &amp; Closed</option>
                  </Input>
                </FormGroup>
                <FormGroup className="mb-0">
                  <Input 
                    type="date" 
                    value={filterDate} 
                    onChange={(e) => { setFilterDate(e.target.value); setCurrentPage(1); }} 
                    placeholder="Date" 
                  />
                </FormGroup>
              </div>
              <div className="d-flex gap-2">
                {selectedIds.length > 0 && (
                  <Button color="danger" onClick={handleBulkDelete}>
                    Delete Selected ({selectedIds.length})
                  </Button>
                )}
                <Button color="primary" onClick={fetchTickets}>Refresh</Button>
              </div>
            </div>

            <Table responsive hover className="table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: '40px', textAlign: 'center', verticalAlign: 'middle' }}>
                    <input 
                      type="checkbox"
                      className="form-check-input mt-0"
                      onChange={handleSelectAll}
                      checked={tickets.length > 0 && selectedIds.length === tickets.length}
                    />
                  </th>
                  <th>ID</th>
                  <th>Customer (B2B)</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="9" className="text-center">Loading...</td></tr>
                ) : tickets.length === 0 ? (
                  <tr><td colSpan="9" className="text-center">No tickets found</td></tr>
                ) : (
                  tickets.map(t => {
                    const isNew = !t.is_opened;
                    return (
                      <tr key={t.id}>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <input 
                            type="checkbox"
                            className="form-check-input mt-0"
                            checked={selectedIds.includes(t.id)}
                            onChange={(e) => handleSelectOne(e, t.id)}
                          />
                        </td>
                        <td>
                          TKT-{t.id}
                          {isNew && (
                            <Badge color="danger" className="ms-2 fw-bold" style={{ fontSize: '9px', padding: '3px 6px' }}>
                              NEW
                            </Badge>
                          )}
                        </td>
                      <td>
                        <strong>{t.org_name}</strong>
                        <br />
                        <small className="text-muted">{t.created_by_name}</small>
                      </td>
                      <td>
                        <span className="fw-semi-bold">{t.subject}</span>
                        {t.booking_id && (
                          <div className="text-muted small mt-1">Booking #{t.booking_id}</div>
                        )}
                      </td>
                      <td className="text-capitalize">{t.category}</td>
                      <td>{renderPriority(t.priority)}</td>
                      <td>{renderStatus(t.status, t)}</td>
                      <td>{new Date(t.created_at).toLocaleString()}</td>
                      <td>
                        {t.connect_requested_at && !t.connected_at && (
                          <Button 
                            color="success" 
                            size="sm" 
                            onClick={() => handleAcceptConnection(t.id, true)} 
                            className="me-2 animate-pulse"
                            style={{ fontWeight: 'bold' }}
                          >
                            Accept & Connect
                          </Button>
                        )}
                        <Button color="info" size="sm" onClick={() => openTicket(t)} className="me-2">
                          Manage
                        </Button>
                        <Button color="danger" size="sm" onClick={() => handleDelete(t.id)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-4 align-items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                <Pagination>
                  <PaginationItem disabled={currentPage <= 1}>
                    <PaginationLink previous onClick={() => setCurrentPage(currentPage - 1)} />
                  </PaginationItem>
                  {[...Array(totalPages)].map((_, i) => (
                    <PaginationItem active={i + 1 === currentPage} key={i}>
                      <PaginationLink onClick={() => setCurrentPage(i + 1)}>
                        {i + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem disabled={currentPage >= totalPages}>
                    <PaginationLink next onClick={() => setCurrentPage(currentPage + 1)} />
                  </PaginationItem>
                </Pagination>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', color: '#6b7280', fontWeight: 500 }}>
                  <span>Go to page</span>
                  <input
                    type="number"
                    min={1}
                    max={totalPages}
                    value={pageInput}
                    onChange={handlePageInputChange}
                    onKeyDown={handlePageInputKeyDown}
                    onBlur={handlePageInputBlur}
                    style={{
                      width: '52px',
                      padding: '6px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#374151',
                      textAlign: 'center',
                      outline: 'none',
                    }}
                  />
                  <span>of {totalPages}</span>
                </div>
              </div>
            )}
          </Widget>
        </Col>
      </Row>

      {/* Detail Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} size="xl">
        <ModalHeader toggle={toggleModal}>
          Manage Ticket TKT-{selectedTicket?.id}
        </ModalHeader>
        <ModalBody>
          {selectedTicket && (
            <Row>
              {/* Left Column: Metadata, Info, Status updating */}
              <Col md={5} className="border-end">
                <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: '15px' }}>
                  <h5 className="fw-bold mb-3 text-dark">Ticket Details</h5>
                  
                  <div 
                    className="p-3 mb-3" 
                    style={{ 
                      fontSize: '0.85rem', 
                      borderRadius: '12px', 
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                    }}
                  >
                    <p className="mb-2"><strong>Customer (B2B):</strong> <span className="fw-semibold text-dark">{selectedTicket.org_name}</span></p>
                    <p className="mb-2">
                      <strong>Emergency contact:</strong>{' '}
                      {selectedTicket.account_phone ? (
                        <a
                          href={formatTelHref(selectedTicket.account_phone)}
                          className="fw-bold text-decoration-none"
                          style={{ color: '#0d6efd' }}
                        >
                          {selectedTicket.account_phone}
                        </a>
                      ) : (
                        <span className="fw-semibold text-dark">Not on file</span>
                      )}
                    </p>
                    <p className="mb-2"><strong>Opened By:</strong> <span className="fw-semibold text-dark">{selectedTicket.created_by_name}</span></p>
                    <p className="mb-2"><strong>Booking:</strong> <span className="fw-semibold text-dark">{selectedTicket.booking_id ? `Booking #${selectedTicket.booking_id}` : 'N/A'}</span></p>
                    <p className="mb-2"><strong>Category:</strong> <span className="text-capitalize fw-semibold text-dark">{selectedTicket.category}</span></p>
                    <p className="mb-2"><strong>Priority:</strong> {renderPriority(selectedTicket.priority)}</p>
                    <p className="mb-0"><strong>Created On:</strong> <span className="fw-semibold text-dark">{new Date(selectedTicket.created_at).toLocaleString('en-IN')}</span></p>
                  </div>

                  {selectedTicket.connect_requested_at && !selectedTicket.connected_at && (
                    <div 
                      className="p-3 rounded mb-3 text-center border" 
                      style={{ backgroundColor: '#fff8e1', borderColor: '#ffe082', borderRadius: '12px' }}
                    >
                      <p className="mb-2 fw-bold text-warning" style={{ fontSize: '0.85rem' }}>
                        ⚠️ Connection Requested by Client
                      </p>
                      <div className="d-flex gap-2 mt-3">
                        <Button
                          color="success"
                          className="flex-grow-1 text-white fw-bold shadow-sm"
                          style={{ borderRadius: '8px', fontSize: '0.85rem' }}
                          onClick={() => handleAcceptConnection(selectedTicket.id, false)}
                        >
                          Accept
                        </Button>
                        <Button
                          color="secondary"
                          outline
                          className="fw-bold shadow-sm"
                          style={{ borderRadius: '8px', fontSize: '0.85rem' }}
                          onClick={() => handleDeclineConnection(selectedTicket.id)}
                        >
                          Decline
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {selectedTicket.connected_at && (
                    <div 
                      className="p-3 rounded mb-3 border" 
                      style={{ 
                        backgroundColor: '#e8f5e9', 
                        borderColor: '#a5d6a7', 
                        fontSize: '0.8rem', 
                        color: '#1b5e20',
                        borderRadius: '12px'
                      }}
                    >
                      <p className="mb-2 fw-bold">✓ Connected Live Session</p>
                      <p className="mb-1"><strong>Session ID:</strong> <code style={{ color: '#1b5e20' }}>{selectedTicket.live_session_id}</code></p>
                      <p className="mb-0"><strong>Connected At:</strong> {new Date(selectedTicket.connected_at).toLocaleString('en-IN')}</p>
                    </div>
                  )}

                  {!selectedTicket.connected_at && selectedTicket.live_session_closed_by && (
                    <div
                      className="p-3 rounded mb-3 border"
                      style={{
                        backgroundColor: '#eceff1',
                        borderColor: '#b0bec5',
                        fontSize: '0.8rem',
                        color: '#37474f',
                        borderRadius: '12px',
                      }}
                    >
                      <p className="mb-1 fw-bold">Live session ended</p>
                      <p className="mb-0">
                        <strong>Closed by:</strong>{' '}
                        {CLOSED_BY_LABELS[selectedTicket.live_session_closed_by]
                          || selectedTicket.live_session_closed_by}
                        {selectedTicket.live_session_closed_at && (
                          <>
                            {' '}
                            · {new Date(selectedTicket.live_session_closed_at).toLocaleString('en-IN')}
                          </>
                        )}
                      </p>
                    </div>
                  )}

                  <h5 className="fw-bold mt-4 mb-2 text-dark">Subject & Description</h5>
                  <div 
                    className="mb-3 p-3" 
                    style={{ 
                      borderRadius: '12px',
                      borderLeft: '4px solid #4f46e5',
                      backgroundColor: '#f9fafb'
                    }}
                  >
                    <h6 className="fw-bold text-dark mb-2" style={{ fontSize: '0.95rem' }}>{selectedTicket.subject}</h6>
                    <p
                      className="mb-0 fw-semibold text-dark"
                      style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: '1.6', color: '#1f2937' }}
                    >
                      {selectedTicket.description}
                    </p>
                  </div>

                  <hr className="my-4" />

                  <FormGroup className="mb-3">
                    <Label className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>Update Status</Label>
                    <Input
                      type="select"
                      value={updateData.status}
                      onChange={(e) => setUpdateData({ ...updateData, status: e.target.value })}
                      style={{ borderRadius: '8px', fontSize: '0.85rem' }}
                    >
                      <option value="raised">Raised</option>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved &amp; Closed</option>
                      <option value="cancelled">Cancelled</option>
                    </Input>
                  </FormGroup>

                  <FormGroup className="mb-3">
                    <Label className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>Internal / Summary Admin Notes</Label>
                    <Input
                      type="textarea"
                      rows="3"
                      value={updateData.admin_notes}
                      onChange={(e) => setUpdateData({ ...updateData, admin_notes: e.target.value })}
                      placeholder="Notes or final summary response..."
                      style={{ borderRadius: '8px', fontSize: '0.85rem' }}
                    />
                  </FormGroup>
                </div>
              </Col>

              {/* Right Column: Dynamic Conversational Thread */}
              <Col md={7} className="d-flex flex-column" style={{ height: '65vh' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="fw-bold mb-0 text-dark">Discussion Thread</h5>
                  <div className="d-flex align-items-center gap-2">
                    {selectedTicket.connected_at && (
                      <Button
                        color="outline-danger"
                        size="sm"
                        className="py-1 px-3 rounded-pill uppercase fw-bold"
                        style={{ fontSize: '10px', letterSpacing: '0.5px' }}
                        onClick={() => handleCloseConnection(selectedTicket.id)}
                      >
                        Close Session
                      </Button>
                    )}
                    {selectedTicket.connected_at ? (
                      <Badge color="success" className="p-2 px-3 rounded-pill uppercase fw-bold" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                        ● Live
                      </Badge>
                    ) : selectedTicket.live_session_closed_by ? (
                      <Badge color="dark" className="p-2 px-3 rounded-pill uppercase fw-bold" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                        Session ended
                      </Badge>
                    ) : (
                      <Badge color="secondary" className="p-2 px-3 rounded-pill uppercase fw-bold" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                        Not connected
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Conversation feed box */}
                <div 
                  className="border rounded p-3 mb-3 d-flex flex-column" 
                  style={{ 
                    flex: 1, 
                    overflowY: 'auto', 
                    gap: '16px', 
                    minHeight: '350px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '12px'
                  }}
                >
                  {messages.length === 0 ? (
                    <div className="text-center my-auto text-muted small p-4">
                      <div className="mb-2 text-secondary">
                        <i className="fa fa-comments-o fa-2x" />
                      </div>
                      <p className="mb-0 fw-bold">No replies exchanged yet.</p>
                      <p className="mb-0 text-secondary" style={{ fontSize: '0.75rem' }}>Type below to send a reply to the B2B team.</p>
                    </div>
                  ) : (
                    messages.map((msg) => {
                      const isMe = msg.sender_type === "admin";
                      return (
                        <div 
                          key={msg.id} 
                          className={`d-flex flex-column ${isMe ? 'align-items-end' : 'align-items-start'}`}
                          style={{ maxWidth: '80%', alignSelf: isMe ? 'flex-end' : 'flex-start' }}
                        >
                          <small className="text-muted mb-1 px-1" style={{ fontSize: '10px', fontWeight: '500' }}>
                            {isMe ? "You" : (msg.sender_username || "Client")} • {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </small>
                          <div 
                            className="p-3 shadow-sm" 
                            style={{ 
                              fontSize: '0.8rem', 
                              backgroundColor: isMe ? '#e3f2fd' : '#ffffff', 
                              color: isMe ? '#0d47a1' : '#2c3e50',
                              border: isMe ? '1px solid #bbdefb' : '1px solid #e9ecef',
                              borderRadius: isMe ? '16px 16px 0 16px' : '16px 16px 16px 0',
                              whiteSpace: 'pre-wrap',
                              fontWeight: '500',
                              lineHeight: '1.4'
                            }}
                          >
                            {msg.message_text && <p className="mb-0">{msg.message_text}</p>}
                            {msg.attachments && msg.attachments.length > 0 && (
                              <div className="mt-2" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {msg.attachments.map((rawAtt, idx) => {
                                  const att = normalizeAttachment(rawAtt);
                                  const attachmentUrl = resolveSupportAttachmentUrl(att);
                                  if (!attachmentUrl) return null;
                                  if (att.type === 'image') {
                                    return (
                                      <a key={idx} href={attachmentUrl} target="_blank" rel="noreferrer" className="d-block overflow-hidden border rounded" style={{ maxWidth: '180px' }}>
                                        <img src={attachmentUrl} alt={att.name} style={{ maxHeight: '120px', width: '100%', objectFit: 'cover', cursor: 'pointer' }} />
                                      </a>
                                    );
                                  }
                                  if (att.type === 'video') {
                                    return (
                                      <div key={idx} className="overflow-hidden border rounded bg-black" style={{ maxWidth: '180px' }}>
                                        <video src={attachmentUrl} controls style={{ maxHeight: '120px', width: '100%' }} />
                                        <a href={attachmentUrl} target="_blank" rel="noreferrer" className="d-block text-center py-1 text-white text-decoration-none fw-bold" style={{ fontSize: '10px', backgroundColor: '#343a40' }}>
                                          Open Video ↗
                                        </a>
                                      </div>
                                    );
                                  }
                                  return (
                                    <a
                                      key={idx}
                                      href={attachmentUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="d-flex align-items-center gap-1.5 p-2 rounded text-decoration-underline"
                                      style={{
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        backgroundColor: isMe ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.05)',
                                        color: isMe ? '#0d47a1' : '#2c3e50'
                                      }}
                                    >
                                      📎 <span className="text-truncate" style={{ maxWidth: '110px' }}>{att.name}</span>
                                    </a>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message input */}
                <div>
                  {/* Attachment Previews */}
                  {attachedFiles.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {attachedFiles.map((f, idx) => (
                        <Badge 
                          key={idx} 
                          color="info" 
                          className="d-flex align-items-center gap-1.5 p-2 px-3 rounded-pill text-white fw-bold"
                          style={{ fontSize: '10px' }}
                        >
                          <span className="text-truncate" style={{ maxWidth: '140px' }}>{f.name}</span>
                          <span 
                            style={{ cursor: 'pointer', fontWeight: 'bold' }} 
                            onClick={() => setAttachedFiles((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            ×
                          </span>
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="d-flex gap-2 align-items-center">
                    {/* Attachment trigger */}
                    <Label 
                      className="btn btn-outline-secondary mb-0 d-flex align-items-center justify-content-center"
                      style={{ borderRadius: '8px', padding: '10px 14px', cursor: 'pointer' }}
                    >
                      <input 
                        type="file"
                        accept="image/*,video/*,.pdf,.doc,.docx"
                        onChange={handleFileUpload} 
                        style={{ display: 'none' }} 
                        disabled={isUploading}
                      />
                      {isUploading ? (
                        <i className="fa fa-spinner fa-spin" />
                      ) : (
                        <i className="fa fa-paperclip" />
                      )}
                    </Label>

                    <Input
                      type="text"
                      placeholder={
                        selectedTicket.live_session_closed_by
                          ? "Live session ended — connection is closed."
                          : !selectedTicket.connected_at
                            ? "Accept the live connection to start chatting…"
                            : isUploading
                              ? "Uploading attachment..."
                              : "Type a reply and press Enter..."
                      }
                      value={newMessage}
                      disabled={!isLiveSessionActive(selectedTicket) || isUploading}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      style={{ fontSize: '0.85rem', borderRadius: '8px', padding: '10px 12px', flex: 1 }}
                    />

                    <Button 
                      color="primary" 
                      onClick={handleSendMessage} 
                      disabled={!isLiveSessionActive(selectedTicket) || ((!newMessage.trim() && attachedFiles.length === 0) || isUploading)}
                      style={{ borderRadius: '8px', paddingLeft: '20px', paddingRight: '20px', fontWeight: 'bold', height: '42px' }}
                    >
                      Send
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter style={{ borderTop: '1px solid #e9ecef' }}>
          <Button color="secondary" onClick={toggleModal} style={{ borderRadius: '8px' }}>Cancel</Button>
          <Button color="success" onClick={handleUpdate} style={{ borderRadius: '8px', fontWeight: 'bold' }}>Save Changes & Status</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SupportTicketsPage;
